import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
    Container,
    Alert,
    useTheme,
} from '@mui/material';
import { useExplorerDates } from './Explorer/useExplorerDates';
import { useAuth } from '../contexts/AuthContext';
import DayRangePicker from '../components/DayRangePicker';
import { ExplorerHeader } from './Explorer/ExplorerHeader';
import ExplorerExportButton from './Explorer/ExplorerExportButton';
import type { ExportConfig } from '../export';
import { exportAlternatives, LIVESTREAM_PROGRAM_REPORT_TYPE } from './Explorer/explorerTabs';
import { SummarySection } from './Explorer/SummarySection';
import { LivestreamDetailRecentList } from './Explorer/LivestreamDetailRecentList';
import { LivestreamWeeklyTrendsSection } from './Explorer/LivestreamWeeklyTrendsSection';
import { LivestreamProgramPeriodSection } from './Explorer/LivestreamProgramPeriodSection';
import {
    computeLivestreamDetailSummaryFromStreams,
    isMongoObjectIdString,
    formatLivestreamOnDemandViewsCount,
    formatViewerIntegerFloor,
    GC_TIME,
    LIVESTREAM_DETAIL_SUMMARY_TOOLTIPS,
    LIVESTREAM_METRIC_LABELS,
    STALE_TIME,
} from './Explorer/utils';
import apiService from '../services/api';
import { LivestreamStreamProgramEditDialog } from './Explorer/LivestreamStreamProgramEditDialog';
import { getS3Url, ensureDirectS3Url } from '../utils/imageUtils';
import { ROUTES_NAMES } from '../components/routes';
import {
    mergeExplorerLivestreamsWithLive,
    useExplorerLiveStreamsQuery,
} from './Explorer/explorerLiveStreams';

const UNASSIGNED_LIVESTREAM_PROGRAM_ROUTE_SLUGS = new Set(['none', 'unassigned', 'sin-programa']);

function isUnassignedProgramExplorerRoute(programId: string | undefined): boolean {
    if (!programId) return true;
    return UNASSIGNED_LIVESTREAM_PROGRAM_ROUTE_SLUGS.has(programId.toLowerCase());
}

const ExplorerLivestreamProgramDetail: React.FC = () => {
    const { t } = useTranslation('explorer');
    const theme = useTheme();
    const { programId, channelId: pathChannelId } = useParams<{
        programId: string;
        channelId?: string;
    }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const explorerFilters = React.useMemo(
        () => ({
            country: searchParams.get('country') || undefined,
            category: searchParams.get('category') || undefined,
            channel:
                pathChannelId || searchParams.get('channel') || undefined,
        }),
        [searchParams, pathChannelId],
    );
    const [programEditStreamId, setProgramEditStreamId] = useState<string | null>(null);

    const { getDateFilterMinStartDate, getDateFilterMaxEndDate, isAdmin } = useAuth();
    const { startDate, endDate, setRange, withDates } = useExplorerDates();
    const currentMinDate = getDateFilterMinStartDate();
    const currentMaxDate = getDateFilterMaxEndDate();

    const {
        data,
        isLoading,
        error,
        isFetching,
        isPlaceholderData,
    } = useQuery({
        queryKey: [
            'explorer-livestream-program',
            programId,
            startDate,
            endDate,
            explorerFilters.country,
            explorerFilters.category,
            explorerFilters.channel,
            'duration-v2',
            'slice-v10',
            'unassigned-channel',
        ],
        queryFn: () =>
            apiService.getExplorerLivestreamProgram(
                programId!,
                startDate,
                endDate,
                explorerFilters,
            ),
        enabled: !!programId,
        staleTime: STALE_TIME,
        gcTime: GC_TIME,
        placeholderData: keepPreviousData,
    });

    const liveQuery = useExplorerLiveStreamsQuery(!!programId);
    const livestreams = useMemo(
        () =>
            mergeExplorerLivestreamsWithLive(
                data?.livestreams ?? [],
                liveQuery.data?.streams,
                liveQuery.isSuccess,
            ),
        [data?.livestreams, liveQuery.data?.streams, liveQuery.isSuccess],
    );

    const {
        data: trendsData,
        isFetching: trendsFetching,
        isPlaceholderData: trendsIsPlaceholderData,
    } = useQuery({
        queryKey: [
            'explorer-livestream-program-trends',
            programId,
            startDate,
            endDate,
            explorerFilters.country,
            explorerFilters.category,
            explorerFilters.channel,
        ],
        queryFn: () =>
            apiService.getExplorerLivestreamProgramTrends(
                programId!,
                startDate,
                endDate,
                explorerFilters,
            ),
        enabled: !!programId && isUnassignedProgramExplorerRoute(programId),
        staleTime: STALE_TIME,
        gcTime: GC_TIME,
        placeholderData: keepPreviousData,
    });

    const onScreenSummary = useMemo(
        () => computeLivestreamDetailSummaryFromStreams(livestreams),
        [livestreams],
    );

    const programName = data?.program.name;
    const exportChannelId =
        explorerFilters.channel && isMongoObjectIdString(explorerFilters.channel)
            ? explorerFilters.channel
            : typeof data?.program.channelMongoId === 'string' &&
                isMongoObjectIdString(data.program.channelMongoId)
              ? data.program.channelMongoId
              : undefined;
    const exportConfig = useMemo<ExportConfig | undefined>(() => {
        if (!programId) return undefined;
        return {
            reportType: LIVESTREAM_PROGRAM_REPORT_TYPE,
            params: {
                startDate,
                endDate,
                programId,
                ...explorerFilters,
                ...(exportChannelId ? { channel: exportChannelId } : {}),
            },
            contextLabels: programName ? [{ label: t('program'), value: programName }] : [],
            alternatives: exportAlternatives([LIVESTREAM_PROGRAM_REPORT_TYPE]),
        };
    }, [programId, programName, startDate, endDate, explorerFilters, exportChannelId, t]);

    if (isLoading && !data) {
        const assigned = !isUnassignedProgramExplorerRoute(programId);
        const knownChannelId =
            pathChannelId && isMongoObjectIdString(pathChannelId)
                ? pathChannelId
                : explorerFilters.channel && isMongoObjectIdString(explorerFilters.channel)
                  ? explorerFilters.channel
                  : undefined;

        return (
            <Container maxWidth="xl" sx={{ pb: 4 }}>
                <ExplorerHeader
                    loading
                    onBack={() => {
                        if (knownChannelId) {
                            navigate(
                                withDates(
                                    generatePath(ROUTES_NAMES.EXPLORER_LIVESTREAM_CHANNEL, {
                                        channelId: knownChannelId,
                                    }),
                                ),
                                { replace: true },
                            );
                            return;
                        }
                        navigate(withDates(ROUTES_NAMES.EXPLORER_LIVESTREAMS_LIST), {
                            replace: true,
                        });
                    }}
                    image={{ name: '' }}
                    title=""
                    subtitle=""
                >
                    <DayRangePicker
                        startDate={startDate}
                        endDate={endDate}
                        onChange={setRange}
                        minDate={currentMinDate}
                        maxDate={currentMaxDate}
                    />
                    <ExplorerExportButton config={exportConfig} />
                </ExplorerHeader>

                {assigned ? (
                    <LivestreamProgramPeriodSection
                        channelId={knownChannelId ?? ''}
                        programId={programId ?? ''}
                        startDate={startDate}
                        endDate={endDate}
                        country={explorerFilters.country}
                        category={explorerFilters.category}
                        forceLoading={!knownChannelId}
                        livestreams={[]}
                        variant="program"
                    />
                ) : (
                    <>
                        <SummarySection
                            title={t('periodSummary')}
                            isLoading
                            cards={[
                                { title: t('livestreams'), value: '' },
                                { title: t('views'), value: '' },
                                { title: t('viewsAvgShort'), value: '' },
                                { title: LIVESTREAM_METRIC_LABELS.avgEsp, value: '' },
                                { title: LIVESTREAM_METRIC_LABELS.avgPeakEsp, value: '' },
                                { title: t('hoursTotal'), value: '' },
                            ]}
                        />
                        <LivestreamWeeklyTrendsSection trends={[]} isLoading />
                    </>
                )}

                <LivestreamDetailRecentList
                    variant="program"
                    livestreams={[]}
                    isLoading
                />
            </Container>
        );
    }

    if (error || !data) {
        return (
            <Container maxWidth="xl" sx={{ pb: 4 }}>
                <Alert severity="error">{t('loadProgramError')}</Alert>
            </Container>
        );
    }

    const { program } = data;
    const pageAssignedProgramId = isUnassignedProgramExplorerRoute(programId) ? null : programId!;
    const pageAssignedProgramName = program.name;
    const showDetailContentLoading =
        (isLoading && !data) || (isFetching && isPlaceholderData);
    const showTrendsSkeleton =
        trendsFetching && (!trendsData || trendsIsPlaceholderData);
    const handleBack = () => {
        const backChannelId =
            pathChannelId && isMongoObjectIdString(pathChannelId)
                ? pathChannelId
                : explorerFilters.channel && isMongoObjectIdString(explorerFilters.channel)
                  ? explorerFilters.channel
                  : typeof program.channelMongoId === 'string' &&
                      isMongoObjectIdString(program.channelMongoId)
                    ? program.channelMongoId
                    : undefined;
        if (backChannelId) {
            navigate(
                withDates(
                    generatePath(ROUTES_NAMES.EXPLORER_LIVESTREAM_CHANNEL, {
                        channelId: backChannelId,
                    }),
                ),
                { replace: true },
            );
            return;
        }
        navigate(withDates(ROUTES_NAMES.EXPLORER_LIVESTREAMS_LIST), { replace: true });
    };

    const periodChannelId =
        pathChannelId && isMongoObjectIdString(pathChannelId)
            ? pathChannelId
            : explorerFilters.channel && isMongoObjectIdString(explorerFilters.channel)
              ? explorerFilters.channel
              : program.channelMongoId;
    const showProgramPeriod =
        !isUnassignedProgramExplorerRoute(programId) && Boolean(programId && periodChannelId);

    return (
        <Container maxWidth="xl" sx={{ pb: 4 }}>
            <ExplorerHeader
                onBack={handleBack}
                image={{
                    name: program.name,
                    src: (() => {
                        const key = program.imageS3Key || program.channelImageS3Key;
                        return key ? ensureDirectS3Url(getS3Url(key)) : undefined;
                    })(),
                }}
                title={program.name}
                subtitle={
                    program.channelName
                        ? program.channelName
                        : t('livestreamProgramFallback')
                }
            >
                <DayRangePicker
                    startDate={startDate}
                    endDate={endDate}
                    onChange={setRange}
                    minDate={currentMinDate}
                    maxDate={currentMaxDate}
                />
                <ExplorerExportButton config={exportConfig} />
            </ExplorerHeader>

            {isUnassignedProgramExplorerRoute(programId) ? (
                <>
                    <SummarySection
                        title={t('periodSummary')}
                        isLoading={showDetailContentLoading}
                        valueColor={theme.palette.text.primary}
                        cards={[
                            {
                                title: t('livestreams'),
                                tooltip: LIVESTREAM_DETAIL_SUMMARY_TOOLTIPS.livestreams,
                                value: program.episodeCount,
                            },
                            {
                                title: t('views'),
                                tooltip: LIVESTREAM_DETAIL_SUMMARY_TOOLTIPS.viewsTotales,
                                value: formatLivestreamOnDemandViewsCount(onScreenSummary.totalViews),
                            },
                            {
                                title: t('viewsAvgShort'),
                                tooltip: LIVESTREAM_DETAIL_SUMMARY_TOOLTIPS.viewsProm,
                                value: formatLivestreamOnDemandViewsCount(onScreenSummary.viewsPerStream),
                            },
                            {
                                title: LIVESTREAM_METRIC_LABELS.avgEsp,
                                tooltip: LIVESTREAM_DETAIL_SUMMARY_TOOLTIPS.avgEsp,
                                value: formatViewerIntegerFloor(program.metrics?.avgViewers || 0),
                            },
                            {
                                title: LIVESTREAM_METRIC_LABELS.avgPeakEsp,
                                tooltip: LIVESTREAM_DETAIL_SUMMARY_TOOLTIPS.avgPeakEsp,
                                value: formatViewerIntegerFloor(program.metrics?.avgPeakViewers || 0),
                            },
                            {
                                title: t('hoursTotal'),
                                tooltip: LIVESTREAM_DETAIL_SUMMARY_TOOLTIPS.horasTotales,
                                value: `${Math.round((program.metrics?.totalDurationMinutes || 0) / 60)}h`,
                            },
                        ]}
                    />

                    <LivestreamWeeklyTrendsSection
                        trends={trendsData?.trends ?? []}
                        isLoading={showTrendsSkeleton}
                    />
                </>
            ) : null}

            {showProgramPeriod && programId && periodChannelId ? (
                <LivestreamProgramPeriodSection
                    channelId={periodChannelId}
                    programId={programId}
                    startDate={startDate}
                    endDate={endDate}
                    country={explorerFilters.country}
                    category={explorerFilters.category}
                    livestreams={livestreams}
                    variant="program"
                />
            ) : null}

            <LivestreamDetailRecentList
                variant="program"
                livestreams={livestreams}
                isLoading={showDetailContentLoading}
                isFetching={isFetching && !showDetailContentLoading}
                emptyMessage={t('noLivestreamsInPeriod')}
                isAdmin={isAdmin}
                onEditProgram={setProgramEditStreamId}
            />

            {isAdmin && programEditStreamId ? (
                <LivestreamStreamProgramEditDialog
                    open
                    onClose={() => setProgramEditStreamId(null)}
                    streamId={programEditStreamId}
                    channelId={(() => {
                        const row = livestreams.find(
                            (s) => s._id === programEditStreamId || s.livestreamId === programEditStreamId,
                        );
                        const fromStream = row?.channelId?.trim();
                        if (fromStream && isMongoObjectIdString(fromStream)) return fromStream;
                        return program.channelMongoId;
                    })()}
                    channelName={livestreams.find(
                        (s) => s._id === programEditStreamId || s.livestreamId === programEditStreamId,
                    )?.channelName}
                    assignedProgramId={(() => {
                        const row = livestreams.find(
                            (s) => s._id === programEditStreamId || s.livestreamId === programEditStreamId,
                        );
                        const fromStream = row?.livestreamProgramId?.trim();
                        if (fromStream) return fromStream;
                        return pageAssignedProgramId;
                    })()}
                    assignedProgramName={(() => {
                        const row = livestreams.find(
                            (s) => s._id === programEditStreamId || s.livestreamId === programEditStreamId,
                        );
                        return row?.programName?.trim() ?? pageAssignedProgramName;
                    })()}
                    onSuccess={() => undefined}
                />
            ) : null}
        </Container>
    );
};

export default ExplorerLivestreamProgramDetail;

